import { useMutation } from '@tanstack/react-query';
import { useNotify } from 'ra-core';

/**
 * Resolve a full URL to the Moonstar Grow app.
 *
 * @param url - The relative URL path within the Grow app.
 *
 * @example
 * ```ts
 * // https://www.joinmoonstar.app/journeys/123
 * const url = resolveGrowAppLink('/journeys/123');
 * ```
 */
export function resolveGrowAppLink(url: `/${string}`) {
  const baseUrl = 'https://www.joinmoonstar.app';

  return new URL(url, baseUrl).toString();
}

/**
 * Copy a Grow app link to the clipboard.
 * @example
 * ```ts
 * const { mutate: copyLink } = useCopyGrowAppLink();
 *
 * // Copy the link to the clipboard
 * copyLink('/journeys/123');
 * ```
 */
export function useCopyGrowAppLink() {
  const notify = useNotify();

  return useMutation({
    mutationFn: async (url: `/${string}`) => {
      const resolved = resolveGrowAppLink(url);
      await navigator.clipboard.writeText(resolved);
    },
    onSuccess: () => notify('Link copied to clipboard', { type: 'success' }),
    onError: (err) => {
      console.error(err);
      notify('Failed to copy link to clipboard', { type: 'error' });
    },
  });
}
